/**
 * What each marketing source actually produced.
 *
 * Jonathon bought a phone number for the website, one for the yard signs and
 * one for the truck so that this question has an answer: for the money spent on
 * each, how many people rang, how many did we miss, and how many of them bought
 * a roof. Every one of those numbers exists somewhere in the application
 * already — `calls` carries the source it came through, `jobs` carries the
 * source a rep picked — and this is the one place they are put beside each
 * other.
 *
 * Only inbound calls count. An outbound dial is us following somebody up; a
 * source that gets "credited" with the calls we made to it would show the
 * busiest rep as the best billboard.
 *
 * Money is only ever added here, never in the screen (CLAUDE.md rule 3), and
 * the per-job figures come from `jobContractValues` — the same function the
 * pipeline card uses, so the report and the job can never disagree.
 */

import { addCents, cents, type Cents } from "./money.js";
import { dateFilterStart, type DateFilter } from "./search.js";
import { SOLD_STAGES } from "./stage.js";
import type { JobStage } from "./types.js";

/** A call, as this report needs it. A `calls` row satisfies it. */
export interface ReportCall {
  direction: string;
  lead_source: string | null;
  answered: boolean;
  voicemail_at: string | null;
  started_at: string;
  contact_id?: string | null;
}

/** A job, as this report needs it. A `Job` satisfies it. */
export interface ReportJob {
  id: string;
  lead_source: string | null;
  stage: JobStage;
  created_at: string;
}

/**
 * What a call through a number nobody has configured, or a job whose rep never
 * picked a source, is filed under. Shown rather than dropped: an unattributed
 * pile that is bigger than the attributed one is the most useful thing this
 * report can tell anybody.
 */
export const NO_SOURCE = "No source";

export interface SourceRow {
  source: string;
  /** Inbound calls in the window. */
  calls: number;
  /** Of those, the ones nobody picked up. */
  missed: number;
  /** Of the missed ones, the ones that left a message. */
  voicemails: number;
  /** Calls from a number Roof Board has never seen. A first-time caller. */
  newCallers: number;
  /** Jobs created in the window carrying this source. */
  leads: number;
  /** Of those, the ones that reached a sold stage. */
  won: number;
  /** Contract dollars across the won jobs. */
  revenue: Cents;
  /** won / leads. Zero when there are no leads to divide by. */
  closeRate: number;
}

const emptyRow = (source: string): SourceRow => ({
  source,
  calls: 0,
  missed: 0,
  voicemails: 0,
  newCallers: 0,
  leads: 0,
  won: 0,
  revenue: cents(0),
  closeRate: 0,
});

/**
 * Is this timestamp inside the window?
 *
 * The same windows every other list screen offers, off the same function, so
 * that "this month" means one thing across the application. `withinDateFilter`
 * itself is not reused because it insists on a `created_at`, and a call is
 * dated by when it rang.
 *
 * Anything unparseable is excluded: a row we cannot date cannot be counted in a
 * window, and counting it everywhere would make two windows disagree about the
 * same call.
 */
export function withinWindow(
  iso: string | null | undefined,
  filter: DateFilter,
  now: Date = new Date(),
): boolean {
  const start = dateFilterStart(filter, now);
  if (!start) return true;
  if (!iso) return false;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return false;
  return at >= start.getTime();
}

export interface SourceReport {
  rows: SourceRow[];
  /** Every row added up, for the foot of the table. */
  total: SourceRow;
}

/**
 * Calls and won jobs, by the source that produced them.
 *
 * `values` is the contract dollars per job id from `jobContractValues`; a job
 * missing from it is worth nothing yet, which is the honest answer for a lead
 * nobody has priced.
 *
 * Sources with a call but no job — and jobs with no call — both appear. A yard
 * sign that produces twenty calls and no sold work is exactly the finding this
 * table exists to surface, and it can only surface if the row is drawn.
 */
export function callsAndJobsBySource(
  calls: readonly ReportCall[],
  jobs: readonly ReportJob[],
  values: ReadonlyMap<string, Cents>,
  options: { filter?: DateFilter; now?: Date } = {},
): SourceReport {
  const filter = options.filter ?? "all";
  const now = options.now ?? new Date();

  const rows = new Map<string, SourceRow>();
  const row = (source: string | null): SourceRow => {
    const key = source?.trim() || NO_SOURCE;
    const existing = rows.get(key);
    if (existing) return existing;
    const made = emptyRow(key);
    rows.set(key, made);
    return made;
  };

  for (const call of calls) {
    if (call.direction !== "inbound") continue;
    if (!withinWindow(call.started_at, filter, now)) continue;
    const r = row(call.lead_source);
    r.calls += 1;
    if (!call.answered) r.missed += 1;
    if (call.voicemail_at) r.voicemails += 1;
    if (!call.contact_id) r.newCallers += 1;
  }

  // Won revenue is collected per source before being added, so that the money
  // is summed once with `addCents` rather than accumulated in a loop.
  const wonValues = new Map<string, Cents[]>();

  for (const job of jobs) {
    if (!withinWindow(job.created_at, filter, now)) continue;
    const r = row(job.lead_source);
    r.leads += 1;
    if (SOLD_STAGES.includes(job.stage)) {
      r.won += 1;
      const list = wonValues.get(r.source);
      const value = values.get(job.id) ?? cents(0);
      if (list) list.push(value);
      else wonValues.set(r.source, [value]);
    }
  }

  for (const r of rows.values()) {
    r.revenue = addCents(...(wonValues.get(r.source) ?? []));
    r.closeRate = r.leads > 0 ? r.won / r.leads : 0;
  }

  // Busiest first, and the unattributed pile last however big it is — it is a
  // gap to close, not a source to compare against.
  const ordered = [...rows.values()].sort((a, b) => {
    if (a.source === NO_SOURCE) return 1;
    if (b.source === NO_SOURCE) return -1;
    if (b.revenue !== a.revenue) return b.revenue - a.revenue;
    if (b.calls !== a.calls) return b.calls - a.calls;
    return a.source.localeCompare(b.source);
  });

  const total = emptyRow("Total");
  for (const r of ordered) {
    total.calls += r.calls;
    total.missed += r.missed;
    total.voicemails += r.voicemails;
    total.newCallers += r.newCallers;
    total.leads += r.leads;
    total.won += r.won;
  }
  total.revenue = addCents(...ordered.map((r) => r.revenue));
  total.closeRate = total.leads > 0 ? total.won / total.leads : 0;

  return { rows: ordered, total };
}

/**
 * How a call reads on a list: what happened, in three words.
 *
 * The distinction that matters is between a call nobody picked up and one that
 * left a message — the first is somebody to ring back today, the second is
 * somebody to ring back after listening.
 */
export function callOutcome(call: {
  direction: string;
  answered: boolean;
  voicemail_at: string | null;
  status?: string | null;
}): "Voicemail" | "Missed" | "Answered" | "Outbound" | "Ringing" {
  if (call.voicemail_at) return "Voicemail";
  if (call.answered)
    return call.direction === "inbound" ? "Answered" : "Outbound";
  if (call.status === "ringing" || call.status === "in-progress")
    return "Ringing";
  return call.direction === "inbound" ? "Missed" : "Outbound";
}

/** Seconds as a person reads them. 22 → 0:22, 754 → 12:34. */
export function callLength(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "";
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
